/**
 * clientData.js — display layer for client-supplied constituency datasets.
 *
 * The data itself is served by /api/client_data/ (see core/client_data.py); the
 * pipeline that puts it there is load_client_dataset.py. This module holds the
 * bit the API shouldn't care about: what each metric is called in the UI, how
 * it's formatted, and which client may see it.
 *
 * Adding a dataset: add an entry to CLIENT_DATASETS whose `metrics` keys match
 * the column names in core/client_datasets.py.
 */

/** Client slugs that may see each dataset — mirrors the backend registry. */
export const CLIENT_DATASETS = {
  motability_customers: {
    label: "Motability customers",
    shortLabel: "Customer base",
    clients: ["motability-operations", "hanbury", "stack-data-strategy"],
    // The headline count, rendered larger than the rest.
    headline: "customers",
    metrics: {
      customers: {
        label: "Scheme customers",
        format: "count",
        // Counts are absolute, so "vs GB" is a comparison against the mean
        // constituency rather than against the GB total.
        compare: "mean",
      },
      pct_under_16:            { label: "Aged under 16",   format: "pct1" },
      pct_over_65:             { label: "Aged 65+",        format: "pct1" },
      avg_imd:                 { label: "Avg IMD decile",  format: "num1",
                                 help: "1 = most deprived, 10 = least deprived" },
      // `short` is the legend label: the income mix has five bands and the
      // legend has to stay on one line inside a half-width panel.
      pct_low_income:          { label: "Low income",          short: "Low",       format: "pct1", band: "#C62828" },
      pct_middle_income:       { label: "Middle income",       short: "Middle",    format: "pct1", band: "#F57C00" },
      pct_upper_middle_income: { label: "Upper-middle income", short: "Upper-mid", format: "pct1", band: "#0288D1" },
      pct_high_income:         { label: "High income",         short: "High",      format: "pct1", band: "#2E7D32" },
      pct_unknown_income:      { label: "Income unknown",      short: "Unknown",   format: "pct1", band: "#B0B8C4" },
    },
    // Metrics that together make up a 100% distribution, drawn as one bar.
    bands: [
      "pct_low_income", "pct_middle_income", "pct_upper_middle_income",
      "pct_high_income", "pct_unknown_income",
    ],
  },

  mccain_farming: {
    label: "Farming & deprivation profile",
    shortLabel: "Local context",
    clients: ["mccain", "stack-data-strategy"],
    headline: "farm_core_pct",
    metrics: {
      farm_core_pct:   { label: "% land farmed", format: "pct1" },
      // Stored as counts, not a percentage, so the GB figure can be the true
      // ratio of the sums. Divide one by the other to display.
      farming_employed: { label: "Farming jobs",     format: "count", compare: "mean" },
      total_employed:   { label: "Jobs in the seat", format: "count", compare: "mean" },
      population:      { label: "Population",    format: "count", compare: "mean" },
      // Neighbourhoods in the 30% most at-risk areas in Britain for food
      // insecurity — not deprivation in general.
      pop_at_risk:     { label: "Population in high food-insecurity areas", format: "count", compare: "mean" },
      pop_at_risk_pct: { label: "Share in high food-insecurity areas",      format: "pct1" },
      // Per-country positions, not quantities: a GB average of a rank means
      // nothing, so these are rendered as "Nth of M" against the seat only and
      // are excluded from any national comparison.
      deprived_rank:      { label: "Deprivation rank",  format: "num0", rankOf: "country_seat_count" },
      country_seat_count: { label: "Seats in country", format: "num0", hidden: true },
    },
  },
};

/** True when this client slug is entitled to the dataset. */
export function canSeeClientDataset(datasetId, clientSlug) {
  const spec = CLIENT_DATASETS[datasetId];
  if (!spec) return false;
  return spec.clients.includes(String(clientSlug || "").toLowerCase());
}

export function formatClientMetric(value, format) {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  const n = Number(value);
  switch (format) {
    case "count": return Math.round(n).toLocaleString();
    case "pct1":  return `${n.toFixed(1)}%`;
    case "pct0":  return `${Math.round(n)}%`;
    case "num1":  return n.toFixed(1);
    case "num0":  return String(Math.round(n));
    default:      return String(n);
  }
}

/**
 * Fetch one client dataset for an area.
 *
 * Returns `{ available, label, source, area, national, coverage, manifest }`
 * where `area` is the row for `areaCode` (null when that area has no data —
 * client extracts don't always cover all 632 constituencies) and `national` is
 * the GB roll-up. Never throws: a missing dataset or a failed request resolves
 * to `{ available: false }` so callers can just hide the panel.
 */
/**
 * Render-token credentials, read straight off the URL of the headless render
 * page. Returns {} everywhere else, so a normal session request is unchanged.
 */
function renderCredentials() {
  try {
    const q = new URLSearchParams(window.location.search);
    const token = q.get("pdf_token");
    if (!token) return {};
    return {
      pdfToken: token,
      code: q.get("code") || "",
      reportType: q.get("type") || "",
    };
  } catch {
    return {};
  }
}

export async function fetchClientDataset({ datasetId, areaCode }) {
  const unavailable = { available: false, area: null, national: null };
  try {
    const res = await fetch("/api/client_data/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        dataset: datasetId,
        areaCodes: areaCode ? [String(areaCode)] : [],
        // On /report-render there is no Django session: the page is driven by
        // headless Chromium and authorised by the render token in its own URL.
        // Forwarding it lets the server recognise that render job. On every
        // normal page these are absent and the session is used instead.
        ...renderCredentials(),
      }),
    });
    if (!res.ok) {
      console.warn("[clientData] request failed", datasetId, res.status);
      return unavailable;
    }
    const json = await res.json();
    if (!json?.available) return unavailable;

    const area = (json.areas || []).find(
      (r) => String(r.area_cd) === String(areaCode)
    ) || null;

    return {
      available: true,
      label: json.label,
      source: json.source,
      area,
      national: json.national || null,
      coverage: json.coverage || null,
      manifest: json.manifest || null,
    };
  } catch (err) {
    console.warn("[clientData] error", datasetId, err);
    return unavailable;
  }
}
